import React, { useState, useEffect } from 'react';
import { Menu, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
// import { ProfileDialog } from '@/components/ProfileDialog';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { profileAPI } from '@/lib/supabase/profile';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import logo from '@/assets/logos/logo_transparent.png';
import { VehixName } from '@/components/brand/VehixWordmark';

interface MobileHeaderProps {
  onMenuToggle: () => void;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  // const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState<{ profile_completion_percentage?: number } | null>(
    null
  );
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadProfileData();
    }
  }, [user?.id]);

  const loadProfileData = async () => {
    if (!user?.id) return;
    try {
      setLoadingProfile(true);
      const profile = await profileAPI.getUserProfile(user.id);
      setProfileData(profile);
    } catch {
      // Profile fetch is non-blocking; keep header usable even if this fails.
      setProfileData(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/auth');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  const getUserInitials = () => {
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getUserDisplayName = () => {
    return user?.email?.split('@')[0] || 'User';
  };
  return (
    <header
      className='lg:hidden fixed top-0 left-0 right-0 z-30 bg-background
        border-b border-border shadow-sm'
    >
      <div className='flex items-center justify-between px-4 py-3 h-16'>
        {/* Hamburger Menu Button */}
        <Button
          variant='ghost'
          size='sm'
          onClick={onMenuToggle}
          className='h-10 w-10 p-0 hover:bg-accent'
        >
          <Menu className='h-5 w-5' />
        </Button>

        {/* Logo/Brand */}
        <div className='flex items-center gap-3'>
          <div
            className='w-8 h-8 rounded-full bg-card shadow-sm flex
              items-center justify-center overflow-hidden'
          >
            <img src={logo} alt='Vehix' className='h-6 w-6 object-cover' />
          </div>
          <VehixName className='font-bold text-lg text-foreground' />
        </div>

        {/* User Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='sm' className='h-10 w-10 p-0 relative'>
              <Avatar className='h-8 w-8'>
                <AvatarImage src={undefined} alt={getUserDisplayName()} />
                <AvatarFallback className='text-xs bg-primary text-white font-semibold'>
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              {/* Profile completion indicator */}
              {!loadingProfile &&
                profileData &&
                profileData.profile_completion_percentage !== undefined &&
                profileData.profile_completion_percentage < 100 && (
                  <div className='absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full border-2 border-background' />
                )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-56'>
            <DropdownMenuLabel>
              <div className='flex flex-col space-y-1'>
                <p className='text-sm font-medium leading-none'>{getUserDisplayName()}</p>
                <p className='text-xs leading-none text-muted-foreground'>
                  {user?.email || 'user@example.com'}
                </p>
              </div>
            </DropdownMenuLabel>
            {!loadingProfile &&
              profileData &&
              profileData.profile_completion_percentage !== undefined &&
              profileData.profile_completion_percentage < 100 && (
                <>
                  <DropdownMenuSeparator />
                  <div className='px-2 py-1.5'>
                    <div className='text-xs text-muted-foreground mb-1'>Profile completion</div>
                    <div className='flex items-center gap-2'>
                      <div className='flex-1 bg-secondary h-2 rounded-full overflow-hidden'>
                        <div
                          className='h-full bg-primary transition-all duration-300'
                          style={{ width: `${profileData.profile_completion_percentage || 0}%` }}
                        />
                      </div>
                      <span className='text-xs font-medium'>
                        {profileData.profile_completion_percentage || 0}%
                      </span>
                    </div>
                  </div>
                </>
              )}
            <DropdownMenuSeparator />
            {/* <DropdownMenuItem onClick={() => setProfileDialogOpen(true)}>
              <UserCog className='mr-2 h-4 w-4' />
              <span>Profile & Settings</span>
            </DropdownMenuItem> */}
            <DropdownMenuItem onClick={handleSignOut} className='text-red-600 focus:text-red-600'>
              <LogOut className='mr-2 h-4 w-4' />
              <span>Sign Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Profile Dialog */}
        {/* <ProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} /> */}
      </div>
    </header>
  );
};
